import { FlightRoutes } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { ToggleButton } from '@progress/kendo-react-dateinputs';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import React from 'react';
import { FlightStatus } from '../../../../common/enums';
import { DateTimeFormatter, UnitFormatter } from '../../../../formatters';
import commonFormatter from '../../../../formatters/common-formatter';
import useDebugFeatures from '../../../../hooks/use-debug-features';
import {
    AddTextOutline,
    AirplaneOutline,
    CameraOutline,
    ChartsLineSmoothOutline,
    CommentOutline,
    DataInsightsOutline,
    EditOutline,
    FullscreenSolid,
    LocationPinOutline,
    RefreshOutline,
    ShareOutline,
    VisibilityOutline,
    WarningTriangleOutline
} from '../../../../icons/icons';
import { MessageType } from '../../../../services/actor-messages';
import { statusToReadableString } from '../../../components/FlightStatusBadge';
import { FlightDetailsThresholdList } from '../../../components/monitor-status/FlightDetailsThresholdList';
import TimeView from '../../../components/TimeView';
import { EventBus } from '../../../contexts/event-bus';
import { useAppDispatch } from '../../../hooks/store-hooks';
import { useFlightWorker } from '../../../hooks/use-flight-worker';
import { updateFlightPlanSettings, updateRouteFlownSettings, WorkspaceStateItemFlight } from '../../../state/slices/workspace-slice';
import { patchFocusedWorkspace } from '../../../state/store';
import { useFocusedWorkspaceSelector } from '../hooks/use-view';
import './flight-summary.css';
import { FlightDetailsSection } from './FlightDetailsSection';
import { FlightRegistration } from './FlightRegistration';

export default function FlightSummary(props: { flight: WorkspaceStateItemFlight }) {
    const details = props.flight.details;
    const routes = props.flight.routes;
    const dispatch = useAppDispatch();
    const flightPlan = useFocusedWorkspaceSelector((x) => x.flights.settings.flightPlan);
    const routeFlown = useFocusedWorkspaceSelector((x) => x.flights.settings.routeFlown);
    const settings = useFocusedWorkspaceSelector((x) => x.flights.settings);
    const routeAi = useFocusedWorkspaceSelector((x) => x.routeAi);
    const latestRoute = getLatestRoute(routes);
    const worker = useFlightWorker();
    const debug = useDebugFeatures();
    const isActive = details.status === FlightStatus.Active;

    const handleRefreshRouteAi = React.useCallback(() => {
        if (!routeAi) {
            return;
        }

        worker.actor
            .sendAsync({
                type: MessageType.getRouteAi,
                data: {
                    origin: routeAi.origin,
                    destination: routeAi.destination
                }
            })
            .then((r) => {
                dispatch(patchFocusedWorkspace((x) => (x.routeAi = r)));
            });
    }, [routeAi, worker]);

    const handleRouteAi = React.useCallback(() => {
        if (!details.origin || !details.destination) {
            return;
        }

        worker.actor
            .sendAsync({
                type: MessageType.getRouteAi,
                data: {
                    origin: details.origin,
                    destination: details.destination
                }
            })
            .then((r) => {
                dispatch(patchFocusedWorkspace((x) => (x.routeAi = r)));
            });
    }, [details.origin, details.destination, routeAi, worker]);

    const onShowRouteAiChanged = (e: CheckboxChangeEvent) => {
        dispatch(updateFlightPlanSettings('showAi', e.value));
    };

    React.useEffect(() => {
        if (flightPlan.showAi) {
            handleRouteAi();
        } else {
            dispatch(patchFocusedWorkspace((x) => (x.routeAi = null)));
        }
    }, [flightPlan.showAi, details.origin, details.destination]);

    return (
        <div style={{ width: 350 }}>
            {details.breachedThresholdsCount > 0 && (
                <FlightDetailsSection
                    id="flight-summary-breached-thresholds-header"
                    title={`Breached thresholds: (${details.breachedThresholdsCount})`}
                    icon={WarningTriangleOutline}
                    contentClassName="p-0">
                    <FlightDetailsThresholdList flightId={details.id} />
                </FlightDetailsSection>
            )}

            <FlightDetailsSection
                contentClassName="p-0"
                id="flight-details-flight-information"
                title="Flight information"
                icon={AirplaneOutline}
                buttons={
                    <>
                        <ToggleButton
                            id="flight-details-profile-btn"
                            title="Profile"
                            fillMode={'flat'}
                            togglable={true}
                            selected={settings.showProfile}
                            svgIcon={ChartsLineSmoothOutline}
                            onClick={() => dispatch(patchFocusedWorkspace((x) => (x.flights.settings.showProfile = !settings.showProfile)))}
                            className="button-small tooltip-left"
                        />
                        <ToggleButton
                            title="Encompass"
                            fillMode={'flat'}
                            togglable={true}
                            selected={settings.encompass}
                            onClick={() => dispatch(patchFocusedWorkspace((x) => (x.flights.settings.encompass = !settings.encompass)))}
                            svgIcon={FullscreenSolid}
                            className="button-small"
                        />
                        <ToggleButton
                            title="Focus"
                            fillMode={'flat'}
                            togglable={true}
                            selected={settings.focus}
                            onClick={() => dispatch(patchFocusedWorkspace((x) => (x.flights.settings.focus = !settings.focus)))}
                            svgIcon={CameraOutline}
                            className="button-small"
                        />
                        <ToggleButton
                            title="Tag"
                            disabled={!isActive}
                            fillMode={'flat'}
                            togglable={true}
                            selected={settings.showTags}
                            onClick={() => dispatch(patchFocusedWorkspace((x) => (x.flights.settings.showTags = !settings.showTags)))}
                            svgIcon={CommentOutline}
                            className="button-small"
                        />
                    </>
                }>
                <div
                    className="px-3 py-2"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content minmax(max-content, 1fr) max-content 1fr',
                        gridColumnGap: '0.375rem',
                        alignItems: 'center',
                        gridRowGap: '0.25rem',
                        overflow: 'auto'
                    }}>
                    <div className="secondary-text-color">Status</div>
                    <div>{statusToReadableString(details.status, details.ground)}</div>

                    <div className="secondary-text-color">Scratchpad</div>
                    <div className="d-flex align-items-center gap-1 justify-content-between scratchpad-edit-container">
                        {details.scratchpad || '-'}
                        <Button
                            svgIcon={EditOutline}
                            className="button-small scratchpad-edit-btn opacity-0"
                            fillMode="flat"
                            onClick={() => EventBus.fire('show-scratchpad-window', { flightId: details.id })}></Button>
                    </div>

                    <div className="secondary-text-color">Tail #</div>
                    <div>{details.regId ?? '-'}</div>

                    <div className="secondary-text-color">AC Type</div>
                    <div>{details.acType ?? '-'}</div>

                    <div className="secondary-text-color">OUT</div>
                    <div>{DateTimeFormatter.formatTime(details.out) || '-'}</div>

                    <div className="secondary-text-color">OFF</div>
                    <div>{DateTimeFormatter.formatTime(details.off) || '-'}</div>

                    <div className="secondary-text-color">ON</div>
                    <div>{DateTimeFormatter.formatTime(details.on) || '-'}</div>

                    <div className="secondary-text-color">IN</div>
                    <div>{DateTimeFormatter.formatTime(details.in) || '-'}</div>

                    <div className="secondary-text-color">FOB</div>
                    <div>{details.fuelOnBoard?.value ? UnitFormatter.formatFuel(details.fuelOnBoard) : '-'}</div>

                    <div className="secondary-text-color">Desk</div>
                    <div>{details.userField1 || '-'}</div>

                    <div className="secondary-text-color">Beacon</div>
                    <div>{details.beacon?.toString()?.padStart(4, '0') ?? '-'}</div>

                    <div className="secondary-text-color">ICAO ID</div>
                    <div>{details.icaoId?.toString(16).toUpperCase().padStart(6, '0') ?? '-'}</div>
                </div>
            </FlightDetailsSection>

            <FlightDetailsSection
                id="flight-details-flight-position"
                title="Position information"
                icon={LocationPinOutline}
                buttons={
                    <>
                        <ToggleButton
                            disabled={!isActive}
                            fillMode={'flat'}
                            svgIcon={ShareOutline}
                            title="Draw points along route flown"
                            className="button-small"
                            togglable={true}
                            selected={routeFlown.showPoints}
                            onClick={() => dispatch(updateRouteFlownSettings('showPoints', !routeFlown.showPoints))}
                        />
                        <ToggleButton
                            disabled={!isActive}
                            fillMode={'flat'}
                            svgIcon={AddTextOutline}
                            className="button-small"
                            title="Draw labels along route flown"
                            togglable={true}
                            selected={routeFlown.showLabels}
                            onClick={() => dispatch(updateRouteFlownSettings('showLabels', !routeFlown.showLabels))}
                        />
                        <ToggleButton
                            disabled={!isActive}
                            fillMode={'flat'}
                            svgIcon={VisibilityOutline}
                            title="Route flown"
                            className="button-small"
                            togglable={true}
                            selected={routeFlown.show}
                            onClick={() => dispatch(updateRouteFlownSettings('show', !routeFlown.show))}
                        />
                    </>
                }>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content 1fr max-content 1fr',
                        gridColumnGap: '0.375rem',
                        gridRowGap: '0.25rem'
                    }}>
                    <div className="secondary-text-color">Lat</div>
                    <div>{UnitFormatter.formatLat(details.position) ?? '-'}</div>

                    <div className="secondary-text-color">Lon</div>
                    <div>{UnitFormatter.formatLng(details.position) ?? '-'}</div>
                    {debug && (
                        <>
                            <div className="secondary-text-color">LR Lat</div>
                            <div>{UnitFormatter.formatLat(details.lastReal) ?? '-'}</div>

                            <div className="secondary-text-color">LR Lon</div>
                            <div>{UnitFormatter.formatLng(details.lastReal) ?? '-'}</div>

                            <div className="secondary-text-color">LR Speed</div>
                            <div>{details.lastRealSpeed ?? '-'}</div>

                            <div className="secondary-text-color">LR Alt</div>
                            <div>{details.lastRealAltitude ?? '-'}</div>
                        </>
                    )}

                    <div className="secondary-text-color">Updated</div>
                    <div>
                        <TimeView time={details.lastPositionReportTime} count={'up'} /> ago
                    </div>

                    <div className="secondary-text-color">Source</div>
                    <div className="text-nowrap">{commonFormatter.formatFeedId(details.feedId) ?? '-'}</div>

                    <div className="secondary-text-color">Time rem</div>
                    <div>
                        <TimeView time={details.arrivalTime} count={'down'} onGround={details.ground} />
                    </div>

                    <div className="secondary-text-color">Dist rem</div>
                    <div>{UnitFormatter.formatDistance(details.distanceToDestination) ?? '-'}</div>
                </div>
            </FlightDetailsSection>

            <FlightDetailsSection
                id="flight-details-flight-routes"
                title="Route information"
                icon={AirplaneOutline}
                buttons={
                    <>
                        <ToggleButton
                            fillMode={'flat'}
                            svgIcon={DataInsightsOutline}
                            title="Display time/distance labels"
                            className="button-small"
                            togglable={true}
                            selected={flightPlan.showDistanceLabels}
                            onClick={() => dispatch(updateFlightPlanSettings('showDistanceLabels', !flightPlan.showDistanceLabels))}
                        />
                        <ToggleButton
                            fillMode={'flat'}
                            svgIcon={ShareOutline}
                            title="Draw points along planned route"
                            className="button-small"
                            selected={flightPlan.showPoints}
                            togglable={true}
                            onClick={() => dispatch(updateFlightPlanSettings('showPoints', !flightPlan.showPoints))}
                        />
                        <ToggleButton
                            fillMode={'flat'}
                            svgIcon={AddTextOutline}
                            title="Draw labels along planned route"
                            className="button-small"
                            selected={flightPlan.showLabels}
                            togglable={true}
                            onClick={() => dispatch(updateFlightPlanSettings('showLabels', !flightPlan.showLabels))}
                        />
                        <ToggleButton
                            fillMode={'flat'}
                            title="Planned routes"
                            svgIcon={VisibilityOutline}
                            className="button-small"
                            selected={flightPlan.show}
                            togglable={true}
                            onClick={() => dispatch(updateFlightPlanSettings('show', !flightPlan.show))}
                        />
                    </>
                }>
                <div className="d-flex flex-column gap-2">
                    <div className="d-flex flex-column">
                        <div className="flex-grow-1 d-flex gap-1 align-items-center">
                            <div className="secondary-text-color">
                                Planned route {DateTimeFormatter.formatTime(routes.planned?.timestamp)}
                            </div>

                            {latestRoute === 'planned' && <div className="fe-badge">Latest</div>}
                            <div className="flex-grow-1"></div>
                            <Checkbox
                                value={flightPlan.showPlanned}
                                disabled={!routes.planned}
                                onChange={(e) => dispatch(updateFlightPlanSettings('showPlanned', e.value))}
                            />
                        </div>
                        {routes.planned && (
                            <div className="font-mono text-break secondary-bg-color k-rounded-lg p-1">{routes?.planned?.text ?? '-'}</div>
                        )}
                    </div>

                    <div className="d-flex gap-1 flex-column">
                        <div className="d-flex flex-grow-1 gap-1 align-items-center">
                            <div className="secondary-text-color">ATC route {DateTimeFormatter.formatTime(routes.atc?.timestamp)}</div>
                            {latestRoute === 'atc' && <div className="fe-badge">Latest</div>}
                            <div className="flex-grow-1"></div>
                            <Checkbox
                                value={flightPlan.showAtc}
                                disabled={!routes.atc}
                                onChange={(e) => dispatch(updateFlightPlanSettings('showAtc', e.value))}
                            />
                        </div>
                        {routes.atc && (
                            <div className="font-mono text-break secondary-bg-color k-rounded-lg p-1">{routes.atc?.text ?? '-'}</div>
                        )}
                    </div>

                    <div className="d-flex align-items-center">
                        <div className="secondary-text-color flex-grow-1">Route AI (beta)</div>
                        {routeAi && (
                            <div className="d-flex gap-0 pe-2 align-items-center">
                                <Button
                                    fillMode={'flat'}
                                    className="button-small icon-b-1"
                                    svgIcon={RefreshOutline}
                                    onClick={handleRefreshRouteAi}
                                />
                                {DateTimeFormatter.formatTime(routeAi?.time)}
                            </div>
                        )}
                        <Checkbox value={flightPlan.showAi} onChange={onShowRouteAiChanged} />
                    </div>

                    <div className="d-flex">
                        <div className="flex-grow-1 ">
                            <div className="secondary-text-color">Impacts</div>
                        </div>
                        <Checkbox
                            value={settings.impacts}
                            onChange={() => dispatch(patchFocusedWorkspace((x) => (x.flights.settings.impacts = !settings.impacts)))}
                        />
                    </div>
                    <div className="d-flex" id="flight-details-etops-edto">
                        <div className="flex-grow-1 ">
                            <div className="secondary-text-color">ETOPS/EDTO</div>
                        </div>
                        <Checkbox value={flightPlan.showEtops} onChange={(e) => dispatch(updateFlightPlanSettings('showEtops', e.value))} />
                    </div>
                    <div className="d-flex gap-2">
                        <div className="secondary-text-color pe-3">Destination alternates</div>
                        <div>{details.destinationAlternate1}</div>
                        <div>{details.destinationAlternate2}</div>
                    </div>
                </div>
            </FlightDetailsSection>

            <FlightRegistration />
        </div>
    );
}

function getLatestRoute(routes: FlightRoutes): 'atc' | 'planned' | null {
    const { atc, planned } = routes;
    if (atc?.timestamp && planned?.timestamp) {
        return atc.timestamp > planned.timestamp ? 'atc' : 'planned';
    }

    if (atc) {
        return 'atc';
    }

    if (planned) {
        return 'planned';
    }
    return null;
}
